import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { datadogRum } from '@datadog/browser-rum';

export interface DatadogRumConfig {
  applicationId: string;
  clientToken: string;
  site?: string;
  env: string;
  hostedEnvironment: string;
  service: string;
  version: string;
}

export interface DatadogRumUser {
  id: string;
  name?: string;
  email?: string;
  [key: string]: string | undefined;
}

export interface DatadogRumAccount {
  id: string;
  name?: string;
  [key: string]: string | undefined;
}

@Injectable()
export class DatadogRumService {
  private initialized = false;

  connect(config: DatadogRumConfig): void {
    if (!config?.applicationId) return;
    this.initialized = true;
    this.setGlobalContextFromConfig(config);
  }

  private setGlobalContextFromConfig(config: DatadogRumConfig): void {
    // Only what `datadogRum.init` does not already carry. `env`, `service` and `version` are
    // reserved init fields present on every event; copying them here produced a second, later,
    // strictly-worse copy under `@context.*` — absent on anything emitted before this runs.
    //
    // Set per property rather than replacing the context: this runs during DI construction, while
    // `main.ts` writes native_app_version from a promise that can resolve either side of it.
    this.setGlobalContextProperty('hostedEnvironment', config.hostedEnvironment);
    // Distinguish native-app users from mobile-web users (app-vs-browser). iOS-vs-Android
    // is already covered by Datadog's automatic device/OS tags.
    this.setGlobalContextProperty('platform', Capacitor.isNativePlatform() ? 'native' : 'web');
  }

  setUser(user: DatadogRumUser | null): void {
    if (!this.initialized) return;
    try {
      if (user) {
        datadogRum.setUser(user);
      } else {
        datadogRum.clearUser();
      }
    } catch {
      // ignore
    }
  }

  setAccount(account: DatadogRumAccount | null): void {
    if (!this.initialized) return;
    try {
      if (account) {
        datadogRum.setAccount(account);
      } else {
        datadogRum.clearAccount();
      }
    } catch {
      // ignore
    }
  }

  clearUser(): void {
    if (!this.initialized) return;
    try {
      datadogRum.clearUser();
    } catch {
      // ignore
    }
  }

  clearAccount(): void {
    if (!this.initialized) return;
    try {
      datadogRum.clearAccount();
    } catch {
      // ignore
    }
  }

  setGlobalContext(context: Record<string, unknown>): void {
    if (!this.initialized) return;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      datadogRum.setGlobalContext(context as any);
    } catch {
      // ignore
    }
  }

  setGlobalContextProperty(key: string, value: unknown): void {
    if (!this.initialized) return;
    try {
      datadogRum.setGlobalContextProperty(key, value);
    } catch {
      // ignore
    }
  }

  /** Report an error to RUM. `context` lands under `@context` on the error event. */
  addError(error: unknown, context?: object): void {
    if (!this.initialized) return;
    try {
      datadogRum.addError(error, context);
    } catch {
      // ignore
    }
  }

  /** Record a custom RUM action for a genuine user-initiated interaction. */
  addAction(name: string, context?: object): void {
    if (!this.initialized) return;
    try {
      datadogRum.addAction(name, context);
    } catch {
      // ignore
    }
  }

  /**
   * Open a duration vital, to be closed by {@link stopDurationVital} with the same name.
   *
   * Calling this twice for the same name without an intervening stop overwrites the first
   * measurement rather than tracking both, so callers that can overlap must either guard
   * against it or pass a distinct `vitalKey`.
   */
  startDurationVital(name: string, options?: { vitalKey?: string; context?: object; description?: string }): void {
    if (!this.initialized) return;
    try {
      datadogRum.startDurationVital(name, options);
    } catch {
      // ignore
    }
  }

  /** Close a duration vital. `context` is merged with whatever was supplied at start. */
  stopDurationVital(name: string, options?: { vitalKey?: string; context?: object }): void {
    if (!this.initialized) return;
    try {
      datadogRum.stopDurationVital(name, options);
    } catch {
      // ignore
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getSessionId(): string | undefined {
    if (!this.initialized) return undefined;
    try {
      const ctx = datadogRum.getInternalContext?.();
      return (ctx as { session_id?: string } | undefined)?.session_id;
    } catch {
      return undefined;
    }
  }
}
